// frontend/src/components/common/ModalEntrada.jsx
// Modal de entrada de datos — reemplaza window.prompt() del browser
// Uso: const valor = await entrada({ titulo, label, tipo, valorInicial })
// Retorna el string ingresado, o null si el usuario canceló.
// `textoRequerido`: confirmación escrita — el botón (y Enter) quedan bloqueados
// hasta que lo ingresado coincide exactamente con ese texto.
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// ─── Context ──────────────────────────────────────────────────────────────────

const EntradaContext = createContext(null);

const OPCIONES_DEFAULT = {
  titulo:         'Ingresar dato',
  label:          '',
  tipo:           'text',
  valorInicial:   '',
  placeholder:    '',
  requerido:      false,
  textoRequerido: '',
  labelConfirmar: 'Aceptar',
  labelCancelar:  'Cancelar',
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const EntradaProvider = ({ children }) => {
  const [estado, setEstado] = useState({ visible: false, opciones: OPCIONES_DEFAULT });
  const [valor, setValor] = useState('');
  const [errorLocal, setErrorLocal] = useState('');
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  const entrada = useCallback((opciones = {}) => {
    return new Promise((resolve) => {
      const merged = { ...OPCIONES_DEFAULT, ...opciones };
      setEstado({ visible: true, opciones: merged });
      setValor(merged.valorInicial || '');
      // `error` permite reabrir el modal avisando por qué (p. ej. PIN incorrecto).
      setErrorLocal(merged.error || '');
      resolveRef.current = resolve;
    });
  }, []);

  // Auto-focus al abrir
  useEffect(() => {
    if (estado.visible) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [estado.visible]);

  const resolver = useCallback((resultado) => {
    setEstado(s => ({ ...s, visible: false }));
    setErrorLocal('');
    setTimeout(() => resolveRef.current?.(resultado), 120);
  }, []);

  const handleAceptar = useCallback(() => {
    const exigido = estado.opciones.textoRequerido;
    if (exigido && valor.trim() !== exigido) return;
    if (estado.opciones.requerido && !valor.trim()) {
      setErrorLocal('Este campo es obligatorio.');
      return;
    }
    resolver(valor.trim() || null);
  }, [estado.opciones.requerido, estado.opciones.textoRequerido, valor, resolver]);

  const handleCancelar = useCallback(() => {
    resolver(null);
  }, [resolver]);

  const handleKey = useCallback((e) => {
    if (!estado.visible) return;
    if (e.key === 'Enter')  handleAceptar();
    if (e.key === 'Escape') handleCancelar();
  }, [estado.visible, handleAceptar, handleCancelar]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <EntradaContext.Provider value={{ entrada }}>
      {children}
      {estado.visible && (
        <EntradaUI
          opciones={estado.opciones}
          valor={valor}
          onChange={setValor}
          errorLocal={errorLocal}
          inputRef={inputRef}
          onAceptar={handleAceptar}
          onCancelar={handleCancelar}
        />
      )}
    </EntradaContext.Provider>
  );
};

export const useEntrada = () => {
  const ctx = useContext(EntradaContext);
  if (!ctx) throw new Error('useEntrada debe usarse dentro de EntradaProvider');
  return ctx;
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(-8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  animation: ${fadeIn} 0.15s ease both;
  padding: 16px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  animation: ${scaleIn} 0.18s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const TopBar = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.colors.primary};
`;

const Body = styled.div`
  padding: 22px 24px 16px;
`;

const Titulo = styled.h3`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ $error, theme }) => $error ? theme.colors.danger : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const ErrorMsg = styled.span`
  display: block;
  margin-top: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.danger};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px 20px;
`;

const BtnBase = styled.button`
  padding: 8px 18px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  min-width: 90px;
  border: 1px solid transparent;
`;

const BtnCancelar = styled(BtnBase)`
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-color: ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const BtnAceptar = styled(BtnBase)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};

  &:hover { opacity: 0.88; }
  &:active { opacity: 0.75; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// ─── UI ───────────────────────────────────────────────────────────────────────

const EntradaUI = ({ opciones, valor, onChange, errorLocal, inputRef, onAceptar, onCancelar }) => {
  const { titulo, label, tipo, placeholder, labelConfirmar, labelCancelar, textoRequerido } = opciones;
  const bloqueado = !!textoRequerido && valor.trim() !== textoRequerido;

  return (
    <Backdrop onClick={e => e.target === e.currentTarget && onCancelar()}>
      <Card role="dialog" aria-modal="true">
        <TopBar />
        <Body>
          <Titulo>{titulo}</Titulo>
          {label && <Label>{label}</Label>}
          <InputField
            ref={inputRef}
            type={tipo}
            value={valor}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || textoRequerido}
            $error={!!errorLocal}
          />
          {errorLocal && <ErrorMsg>{errorLocal}</ErrorMsg>}
        </Body>
        <Footer>
          <BtnCancelar onClick={onCancelar}>{labelCancelar}</BtnCancelar>
          <BtnAceptar onClick={onAceptar} disabled={bloqueado}>{labelConfirmar}</BtnAceptar>
        </Footer>
      </Card>
    </Backdrop>
  );
};
